import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button, IconButton, TextField, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import RoomIcon from '@material-ui/icons/Room';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import SaveIcon from '@material-ui/icons/Save';
import CloseIcon from '@material-ui/icons/Close';
import AdminHint from '../components/AdminHint';
import AdminViewToggle, { useAdminView } from '../components/AdminViewToggle';

const API_BASE = '/planner-api';

// Gestão de Zonas — sub-divisões de uma Organização (ex.: "Zona Norte", "Zona
// Sul") usadas para a visibilidade de dados. Um utilizador com âmbito 'zona'
// (definido na página Utilizadores) só vê os registos da sua zona.
export default function AdminZones({ token }) {
  const [zones, setZones] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newClientId, setNewClientId] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [view, setView] = useAdminView('zones');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [z, c] = await Promise.all([
        fetch(`${API_BASE}/zones`, { headers }),
        fetch(`${API_BASE}/clients`, { headers }),
      ]);
      setZones(z.ok ? await z.json() : []);
      setClients(c.ok ? await c.json() : []);
    } catch { setZones([]); }
    setLoading(false);
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  async function create() {
    const name = newName.trim();
    if (!name || !newClientId) return;
    await fetch(`${API_BASE}/zones`, { method: 'POST', headers, body: JSON.stringify({ name, clientId: newClientId }) });
    setNewName('');
    load();
  }
  async function saveEdit(id) {
    const name = editName.trim();
    if (!name) return;
    await fetch(`${API_BASE}/zones/${id}`, { method: 'PUT', headers, body: JSON.stringify({ name }) });
    setEditId(null); setEditName('');
    load();
  }
  async function remove(id) {
    if (!window.confirm('Apagar esta zona? Os utilizadores dela ficam sem zona (voltam a ver toda a Organização).')) return;
    await fetch(`${API_BASE}/zones/${id}`, { method: 'DELETE', headers });
    load();
  }

  const orgLabel = (c) => c.ecoogpsClientName || c.name;
  const rowStyle = { display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: 'var(--adm-card, #fff)', border: '1px solid var(--adm-card-border, #e2e8f0)', borderRadius: 10 };
  const thS = { textAlign: 'left', padding: '14px 20px', fontSize: '0.66rem', fontWeight: 700, color: 'var(--adm-text-muted, #94a3b8)', textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid rgba(0,0,0,0.06)', background: 'var(--adm-thead-bg, #f8fafc)' };
  const tdS = { padding: '13px 20px', fontSize: '0.84rem', color: 'var(--adm-text, #0f172a)', borderBottom: '1px solid rgba(0,0,0,0.03)' };
  const orgChip = (z) => <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#0d9488', background: 'rgba(13,148,136,0.08)', padding: '1px 8px', borderRadius: 999 }}>{z.orgName}</span>;
  const editField = (id) => (
    <TextField size="small" variant="outlined" value={editName} autoFocus
      onChange={e => setEditName(e.target.value)} onKeyDown={e => e.key === 'Enter' && saveEdit(id)}
      style={{ maxWidth: 320 }} inputProps={{ style: { fontSize: '0.9rem', fontWeight: 700 } }} />
  );
  const actions = (z) => (editId === z.id ? (
    <>
      <Tooltip title="Guardar"><IconButton size="small" onClick={() => saveEdit(z.id)}><SaveIcon style={{ fontSize: 19, color: '#16a34a' }} /></IconButton></Tooltip>
      <Tooltip title="Cancelar"><IconButton size="small" onClick={() => { setEditId(null); setEditName(''); }}><CloseIcon style={{ fontSize: 19, color: 'var(--adm-text-dim, #64748b)' }} /></IconButton></Tooltip>
    </>
  ) : (
    <>
      <Tooltip title="Renomear"><IconButton size="small" onClick={() => { setEditId(z.id); setEditName(z.name); }}><EditIcon style={{ fontSize: 18, color: 'var(--adm-text-dim, #475569)' }} /></IconButton></Tooltip>
      <Tooltip title="Apagar zona"><IconButton size="small" onClick={() => remove(z.id)}><DeleteOutlineIcon style={{ fontSize: 19, color: 'var(--adm-danger, #ef4444)' }} /></IconButton></Tooltip>
    </>
  ));

  return (
    <Box>
      <Box style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22, background: 'var(--adm-header-bg, #e2f1e9)', border: '1px solid var(--adm-header-border, #bfe0d1)', borderRadius: 14, padding: '18px 22px' }}>
        <Box style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(13,148,136,0.09)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <RoomIcon style={{ color: '#0d9488' }} />
        </Box>
        <Box>
          <Typography style={{ fontSize: '1.72rem', fontWeight: 800, color: 'var(--adm-text, #0f172a)' }}>Zonas</Typography>
          <Typography style={{ fontSize: '0.82rem', color: 'var(--adm-text-dim, #64748b)' }}>
            Sub-divisões de uma Organização (Norte, Sul…) para a visibilidade de dados. O âmbito de cada utilizador define-se na página Utilizadores.
          </Typography>
        </Box>
      </Box>

      <AdminHint
        nodes={[{ text: 'Zona', tone: 'teal' }, { text: 'Organização', tone: 'green' }]}
        labels={['divide a visibilidade dentro de']}
        note="Uma Zona (Norte, Sul…) é uma sub-divisão de uma Organização. Serve para limitar o que cada pessoa vê: um utilizador com âmbito &quot;Só a sua Zona&quot; (definido em Utilizadores) só vê os registos da sua zona."
      />

      {/* Criar */}
      <Box style={{ display: 'flex', gap: 8, marginBottom: 20, maxWidth: 720, flexWrap: 'wrap' }}>
        <FormControl size="small" variant="outlined" style={{ minWidth: 220 }}>
          <InputLabel>Organização</InputLabel>
          <Select label="Organização" value={newClientId} onChange={e => setNewClientId(e.target.value)}>
            <MenuItem value=""><em>— escolher —</em></MenuItem>
            {clients.map(c => <MenuItem key={c.id} value={c.id}>{orgLabel(c)}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" variant="outlined" placeholder="Nome da zona (ex.: Zona Norte)…"
          value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && create()}
          style={{ flex: 1, minWidth: 200 }} inputProps={{ style: { fontSize: '0.85rem' } }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={create} disabled={!newName.trim() || !newClientId}
          style={{ textTransform: 'none', fontWeight: 700, background: 'var(--adm-brand, #2f5d50)', color: '#fff', borderRadius: 8 }}>
          Criar zona
        </Button>
      </Box>

      {!loading && zones.length > 0 && (
        <Box style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
          <AdminViewToggle view={view} onChange={setView} />
        </Box>
      )}

      {loading ? (
        <Box style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><CircularProgress size={30} /></Box>
      ) : zones.length === 0 ? (
        <Typography style={{ color: 'var(--adm-text-muted, #94a3b8)', padding: 20 }}>Sem zonas. Cria a primeira acima.</Typography>
      ) : view === 'table' ? (
        <Box style={{ background: 'var(--adm-card, #fff)', borderRadius: 16, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 1px 2px rgba(20,42,70,0.04), 0 12px 32px rgba(20,42,70,0.07)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thS}>Zona</th>
              <th style={thS}>Organização</th>
              <th style={thS}>Utilizadores</th>
              <th style={{ ...thS, width: 100 }}></th>
            </tr></thead>
            <tbody>
              {zones.map(z => (
                <tr key={z.id}>
                  <td style={tdS}>
                    {editId === z.id ? editField(z.id) : (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <RoomIcon style={{ fontSize: 20, color: '#0d9488' }} />
                        <Typography style={{ fontSize: '0.87rem', fontWeight: 700, color: 'var(--adm-text, #0f172a)' }}>{z.name}</Typography>
                      </Box>
                    )}
                  </td>
                  <td style={tdS}>{orgChip(z)}</td>
                  <td style={tdS}><Typography style={{ fontSize: '0.8rem', color: 'var(--adm-text-dim, #64748b)' }}>{z.userCount}</Typography></td>
                  <td style={tdS}>{actions(z)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      ) : (
        <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {zones.map(z => (
            <Box key={z.id} style={rowStyle}>
              <RoomIcon style={{ fontSize: 22, color: '#0d9488' }} />
              <Box style={{ flex: 1, minWidth: 0 }}>
                {editId === z.id ? editField(z.id) : (
                  <>
                    <Typography style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--adm-text, #0f172a)' }}>
                      {z.name} <span style={{ marginLeft: 6 }}>{orgChip(z)}</span>
                    </Typography>
                    <Typography style={{ fontSize: '0.72rem', color: 'var(--adm-text-muted, #94a3b8)' }}>
                      {z.userCount} utilizador{z.userCount !== 1 ? 'es' : ''}
                    </Typography>
                  </>
                )}
              </Box>
              {actions(z)}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
